(() => {
  const selector = '[data-wabi-delivery-timeline]';
  const defaults = Object.freeze({
    dispatchMinimum: 7,
    dispatchMaximum: 14,
    shippingMinimum: 5,
    shippingMaximum: 7,
  });
  const months = Object.freeze(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);

  const normalizeLocalDate = (value = new Date()) => {
    const date = new Date(value.getTime());
    date.setHours(12, 0, 0, 0);
    return date;
  };

  const addCalendarDays = (value, days) => {
    const date = normalizeLocalDate(value);
    date.setDate(date.getDate() + days);
    return date;
  };

  const addBusinessDays = (value, days, skipSaturday, skipSunday) => {
    const date = normalizeLocalDate(value);
    let added = 0;

    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      const isSkipped = (skipSaturday && day === 6) || (skipSunday && day === 0);
      if (!isSkipped) added += 1;
    }

    return date;
  };

  const parseDays = (value, fallback) => {
    if (value === undefined || value === null || String(value).trim() === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : fallback;
  };

  const normalizeRange = (minimum, maximum, minimumFallback, maximumFallback) => {
    let min = parseDays(minimum, minimumFallback);
    let max = parseDays(maximum, maximumFallback);
    if (min > max) [min, max] = [max, min];
    return { min, max };
  };

  const addConfiguredDays = (date, days, calculation, skipSaturday, skipSunday) => {
    if (calculation === 'business') {
      return addBusinessDays(date, days, skipSaturday, skipSunday);
    }
    return addCalendarDays(date, days);
  };

  const calculateShipRange = (orderDate, options) => ({
    earliest: addConfiguredDays(orderDate, options.dispatch.min, options.dispatchCalculation, options.skipSaturday, options.skipSunday),
    latest: addConfiguredDays(orderDate, options.dispatch.max, options.dispatchCalculation, options.skipSaturday, options.skipSunday),
  });

  const calculateDeliveryRange = (shipRange, options) => ({
    earliest: addConfiguredDays(shipRange.earliest, options.shipping.min, options.shippingCalculation, options.skipSaturday, options.skipSunday),
    latest: addConfiguredDays(shipRange.latest, options.shipping.max, options.shippingCalculation, options.skipSaturday, options.skipSunday),
  });

  const padDay = (date) => String(date.getDate()).padStart(2, '0');

  const formatSingleDate = (date, includeYear = false) => {
    const formatted = `${months[date.getMonth()]} ${padDay(date)}`;
    return includeYear ? `${formatted}, ${date.getFullYear()}` : formatted;
  };

  const formatDateRange = (start, end) => {
    if (start.getTime() === end.getTime()) return formatSingleDate(start);
    const crossesYear = start.getFullYear() !== end.getFullYear();
    return `${formatSingleDate(start, crossesYear)} - ${formatSingleDate(end, crossesYear)}`;
  };

  const readOptions = (element) => ({
    dispatch: normalizeRange(element.dataset.dispatchMin, element.dataset.dispatchMax, defaults.dispatchMinimum, defaults.dispatchMaximum),
    shipping: normalizeRange(element.dataset.shippingMin, element.dataset.shippingMax, defaults.shippingMinimum, defaults.shippingMaximum),
    dispatchCalculation: element.dataset.dispatchCalculation === 'business' ? 'business' : 'calendar',
    shippingCalculation: element.dataset.shippingCalculation === 'business' ? 'business' : 'calendar',
    skipSaturday: element.dataset.skipSaturday !== 'false',
    skipSunday: element.dataset.skipSunday !== 'false',
  });

  const setDateText = (element, name, value) => {
    const target = element.querySelector(`[data-wabi-delivery-date="${name}"]`);
    if (target) target.textContent = value;
  };

  const updateTimeline = (element) => {
    try {
      const orderDate = normalizeLocalDate();
      const options = readOptions(element);
      const shipRange = calculateShipRange(orderDate, options);
      const deliveryRange = calculateDeliveryRange(shipRange, options);

      setDateText(element, 'ordered', formatSingleDate(orderDate));
      setDateText(element, 'ships', formatDateRange(shipRange.earliest, shipRange.latest));
      setDateText(element, 'delivered', formatDateRange(deliveryRange.earliest, deliveryRange.latest));
      element.dataset.wabiDeliveryInitialized = 'true';
    } catch (error) {
      element.dataset.wabiDeliveryInitialized = 'error';
    }
  };

  const initialize = (root = document) => {
    if (root.matches?.(selector)) updateTimeline(root);
    root.querySelectorAll?.(selector).forEach(updateTimeline);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => initialize(event.target));
  document.addEventListener('variant:change', () => initialize());
  document.addEventListener('product:variant-change', () => initialize());
  window.addEventListener('pageshow', () => initialize());
  window.addEventListener('popstate', () => initialize());

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
